import type { InquiryBudget, InquiryDeliverable } from '@/types/api';
import { slugify } from './format';

/**
 * The shape of a kit link: 3 to 40 characters, lowercase letters, digits and
 * hyphens, starting and ending on a letter or digit.
 *
 * The API holds the same expression and is the one that decides. This copy
 * exists so the editor can say what is wrong before a round trip, and so the
 * public page can refuse a path that is not a slug without asking at all.
 */
export const KIT_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/;

/** Why a link cannot be used, as far as can be told without asking. */
export function kitSlugProblem(slug: string): string | null {
  if (!KIT_SLUG_PATTERN.test(slug)) {
    return 'Use 3 to 40 lowercase letters, numbers or hyphens, starting and ending with a letter or number.';
  }
  if (slug.endsWith('secured')) {
    return 'That link cannot end in "secured". Try a different ending.';
  }
  return null;
}

/** A first suggestion for a link, from a name. */
export function kitSlugFromName(name: string): string {
  return slugify(name)
    .slice(0, 40)
    .replace(/^-+|-+$/g, '');
}

/**
 * A URL that is safe to put in an `href` or `src`, or null.
 *
 * Everything the kit links to arrives from the API, which has already checked
 * it — but this page shares an origin with the app, where the session token
 * lives, so a link is only ever rendered if it is plainly `https:`. Anything
 * else, `javascript:` included, renders as text or not at all.
 */
export function safeHttpsUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
}

const compact = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

/** 1,234,567 → 1.2M. Exact below a thousand. */
export function formatCompact(value: number): string {
  return compact.format(value);
}

/** The counts a post can show, in the order they are shown. */
export const KIT_METRIC_ORDER = [
  'views',
  'impressions',
  'likes',
  'comments',
  'shares',
  'quotes',
] as const;

export const KIT_METRIC_LABELS: Record<string, [string, string]> = {
  views: ['view', 'views'],
  impressions: ['impression', 'impressions'],
  likes: ['like', 'likes'],
  comments: ['comment', 'comments'],
  shares: ['share', 'shares'],
  quotes: ['quote', 'quotes'],
};

/** Inquiries per inbox page. */
export const KIT_INQUIRIES_PAGE_SIZE = 20;

/** Topics a kit may show, and how long one may be. The API holds the same. */
export const KIT_MAX_TAGS = 5;
export const KIT_TAG_MAX_LENGTH = 24;

/**
 * A topic as it will be kept: one space between words, no leading `#`, and
 * no longer than a topic may be. Counted in characters, so a Bengali topic is
 * not cut at half the length of an English one.
 */
export function tidyKitTag(raw: string): string {
  const tag = raw.replace(/\s+/g, ' ').trim().replace(/^#+\s*/, '');
  return Array.from(tag).slice(0, KIT_TAG_MAX_LENGTH).join('');
}

/**
 * The budget bands on the "Work with me" form, in the order they are offered.
 * The API accepts exactly these values and stores nothing else.
 */
export const INQUIRY_BUDGETS: { value: InquiryBudget; label: string }[] = [
  { value: 'UNDER_500', label: 'Under $500' },
  { value: 'FROM_500_TO_1500', label: '$500 – $1,500' },
  { value: 'FROM_1500_TO_5000', label: '$1,500 – $5,000' },
  { value: 'OVER_5000', label: '$5,000+' },
  { value: 'OPEN', label: 'Open to discuss' },
];

/** The kinds of work a sponsor can tick. Named by shape, not by platform. */
export const INQUIRY_DELIVERABLES: {
  value: InquiryDeliverable;
  label: string;
}[] = [
  { value: 'SHORT_VIDEO', label: 'Reel / short video' },
  { value: 'DEDICATED_VIDEO', label: 'Dedicated video' },
  { value: 'POST_OR_STORY', label: 'Post or story' },
  { value: 'LONG_TERM', label: 'Long-term ambassador' },
];

export const inquiryBudgetLabel = (value: string | null): string | null =>
  INQUIRY_BUDGETS.find((option) => option.value === value)?.label ?? null;

export const inquiryDeliverableLabel = (value: string): string | null =>
  INQUIRY_DELIVERABLES.find((option) => option.value === value)?.label ?? null;

/** "A", "A and B", "A, B and C". */
export function listOf(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * A platform's name as it reads in a sentence. The account type is part of
 * the label elsewhere ("Facebook Page"); in "followers on Facebook" it is not.
 */
export const platformInWords = (label: string): string =>
  label.replace(/ Page$/, '');

/** The id the "Work with me" section carries, so a button can scroll to it. */
export const KIT_CONTACT_ID = 'work-with-me';
/** The id of the button under the creator's name that leads there. */
export const KIT_CONTACT_BUTTON_ID = 'work-with-me-button';

/** Bring the "Work with me" form into view. */
export function scrollToKitContact(): void {
  const target = document.getElementById(KIT_CONTACT_ID);
  if (!target) return;
  const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  target.scrollIntoView({ behavior: calm ? 'auto' : 'smooth', block: 'start' });
  // For someone not looking at the screen, the move has to be announced:
  // focus goes to the section, without a second jump.
  target.focus({ preventScroll: true });
}

/**
 * Where "Made with ZAAG Social" leads: the home page, saying which kit sent
 * the visitor. `ref` names the kit; the `utm_*` three are what any web
 * analytics tool reads without being told to.
 */
export function kitReferralSearch(slug: string): string {
  const params = new URLSearchParams({
    ref: slug,
    utm_source: 'media_kit',
    utm_medium: 'referral',
    utm_campaign: 'made_with',
  });
  return `?${params.toString()}`;
}
